// ============================================================================
// CONFIG FRAMEWORK SERVICE
// ============================================================================
// Generic Config API. No frontend caching — every screen load / modal open
// re-fetches. Scope cascade resolved on the server.

import { get, put } from '../client/axiosClient';
import { configEndpoints } from '../endpoints/config.endpoints';
import type { ApiResponse } from '../types';
import type {
  ConfigGroup,
  ConfigDefinition,
  ConfigEffectiveValue,
  ConfigScope,
  ConfigScopeRef,
  UpdateConfigRequest,
} from '../types/config.types';

/** Serialize a list of scope refs into the "TYPE:guid,TYPE:guid" form the API expects. */
const serializeScopes = (scopes?: ConfigScopeRef[] | null): string | undefined => {
  if (!scopes || scopes.length === 0) return undefined;
  return scopes.map(s => `${s.scopeType}:${s.scopeId}`).join(',');
};

class ConfigService {
  async getGroups(): Promise<ApiResponse<ConfigGroup[]>> {
    const response = await get<ApiResponse<ConfigGroup[]>>(configEndpoints.groups());
    return response.data;
  }

  async getDefinitions(groupKey: string): Promise<ApiResponse<ConfigDefinition[]>> {
    const response = await get<ApiResponse<ConfigDefinition[]>>(configEndpoints.definitions(groupKey));
    return response.data;
  }

  async getEffective(groupKey: string, scopes?: ConfigScopeRef[]): Promise<ApiResponse<ConfigEffectiveValue[]>> {
    const response = await get<ApiResponse<ConfigEffectiveValue[]>>(
      configEndpoints.effective(groupKey, serializeScopes(scopes))
    );
    return response.data;
  }

  async getEffectiveValue(configKey: string, scopes?: ConfigScopeRef[]): Promise<ApiResponse<{ configKey: string; effectiveValue: string }>> {
    const response = await get<ApiResponse<{ configKey: string; effectiveValue: string }>>(
      configEndpoints.effectiveValue(configKey, serializeScopes(scopes))
    );
    return response.data;
  }

  async updateValues(request: UpdateConfigRequest): Promise<ApiResponse<{ updated: number }>> {
    const response = await put<ApiResponse<{ updated: number }>>(configEndpoints.values(), request);
    return response.data;
  }

  async getScopesByType(scopeType: string): Promise<ApiResponse<ConfigScope[]>> {
    const response = await get<ApiResponse<ConfigScope[]>>(configEndpoints.scopes(scopeType));
    return response.data;
  }
}

export const configService = new ConfigService();
export default configService;
